import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Mail, MailX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNELS = { sms: 'text', email: 'email' };
const WHAT = { setup: 'Setup link', reset: 'Password reset link', course_added: '"New course added" note' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const describe = (s) => {
  const base = describeAccount(s);
  const a = s.access || {};
  if (a.state === 'deactivated') return { tone: 'warn', icon: MailX, title: 'Access deactivated', lines: [`HELPConnect access ended ${fmt(a.deactivated_at)}. Use the HELPConnect Access button to restore it.`] };
  if (a.state === 'ending') return { ...base, lines: [...base.lines, `Access ends ${new Date(a.ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`] };
  return base;
};

const describeAccount = (s) => {
  const m = s.last_message;
  const sentBy = m ? Object.keys(CHANNELS).filter((k) => m.delivery?.[k] === 'sent').map((k) => CHANNELS[k]) : [];
  const lastLine = m && sentBy.length ? `${WHAT[m.type] || 'Access message'} sent by ${sentBy.join(' and ')} on ${fmt(m.sent_at)}` : '';
  if (s.status === 'active') {
    return { tone: 'ok', icon: CheckCircle2, title: 'HELPConnect account set up', lines: [s.last_login_at ? `Last signed in ${fmt(s.last_login_at)}` : s.activated_at ? `Set up on ${fmt(s.activated_at)}` : '', lastLine] };
  }
  if (sentBy.length) return { tone: 'ok', icon: Mail, title: 'HELPConnect access sent', lines: [lastLine, 'The participant has not finished setting up their account yet.'] };
  if (m) {
    const tried = Object.keys(CHANNELS).map((k) => `${CHANNELS[k]} ${m.delivery?.[k] || 'not sent'}`).join(', ');
    return { tone: 'warn', icon: MailX, title: 'HELPConnect access could not be sent', lines: [`Last try on ${fmt(m.sent_at)} (${tried}). Use the HELPConnect Access button to send it.`] };
  }
  return { tone: 'warn', icon: MailX, title: 'HELPConnect access not sent yet', lines: ['Use the HELPConnect Access button to send the participant a setup link.'] };
};

export const HelpConnectAccessStatus = ({ participantId, refreshKey }) => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!participantId) return;
    axios.get(`${API}/participants/${participantId}/helpconnect-access`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [participantId, refreshKey]);
  if (!status) return null;
  const d = describe(status);
  const Icon = d.icon;
  const ok = d.tone === 'ok';
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${ok ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'}`} data-testid="helpconnect-access-status">
      <Icon className={`h-4 w-4 mt-0.5 ${ok ? 'text-green-600' : 'text-yellow-600'}`} />
      <div>
        <p className={`font-medium ${ok ? 'text-green-700 dark:text-green-400' : 'text-yellow-700 dark:text-yellow-400'}`} data-testid="helpconnect-access-status-title">{d.title}</p>
        {d.lines.filter(Boolean).map((l) => <p key={l} className={`text-xs ${ok ? 'text-green-600 dark:text-green-500' : 'text-yellow-600 dark:text-yellow-500'}`}>{l}</p>)}
      </div>
    </div>
  );
};
